import React from 'react';
import { Box, Stack, Typography, Checkbox } from '@mui/material';
import parse from 'html-react-parser';
import { warning } from '_lex/_foundations/phxColors';

// Helper to highlight search matches (copied from Written Directives)
const getHighlightedText = (highlights: any, maxLength: number = 90) => {
  if (!highlights?.texts?.length) return '';
  const hitIndex = highlights.texts.findIndex((text: { type: string }) => text.type === 'hit');
  if (hitIndex === -1) return '';
  const hitText = highlights.texts[hitIndex].value;
  const hitTextLength = hitText.length;
  const remainingLength = maxLength - hitTextLength - 6;
  const perSideLength = Math.floor(remainingLength / 2);
  const beforeText = hitIndex > 0 ? highlights.texts[hitIndex - 1].value : '';
  const afterText = hitIndex < highlights.texts.length - 1 ? highlights.texts[hitIndex + 1].value : '';
  const truncateAtWordBoundary = (text: string, maxLength: number, fromEnd: boolean = false): string => {
    if (text.length <= maxLength) return text;
    if (fromEnd) {
      const truncated = text.substring(text.length - maxLength);
      const firstSpace = truncated.indexOf(' ');
      return firstSpace > 0 ? '...' + truncated.substring(firstSpace + 1) : '...' + truncated;
    } else {
      const truncated = text.substring(0, maxLength);
      const lastSpace = truncated.lastIndexOf(' ');
      return lastSpace > 0 ? truncated.substring(0, lastSpace) + '...' : truncated + '...';
    }
  };
  const truncatedBefore = beforeText ? truncateAtWordBoundary(beforeText, perSideLength, true) : '';
  const truncatedAfter = afterText ? truncateAtWordBoundary(afterText, perSideLength) : '';
  return `${truncatedBefore}<strong>${hitText}</strong>${truncatedAfter}`;
};

const SearchResults: React.FC<{
  results: any[];
  selected: any[];
  onSelect: (item: any) => void;
  onPreview: (item: any) => void;
  activeId: string | null;
}> = ({ results, selected, onSelect, onPreview, activeId }) => {
  return (
    <Stack spacing={0}>
      {results.map((result) => {
        const isChecked = selected.some((item) => item.contentUri === result.contentUri);
        const isActive = activeId === result.contentUri;
        return (
          <Box
            key={result.contentUri}
            sx={{
              cursor: 'pointer',
              mb: 1,
              borderRadius: 1,
              backgroundColor: isActive ? '#E7F3FF' : '#fff',
              '&:hover': { backgroundColor: '#F3F8FE' },
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
            }}
            onClick={() => onPreview(result)}
          >
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                e.stopPropagation();
                onSelect(result);
                onPreview(result);
              }}
              sx={{ mr: 1 }}
              onClick={(e) => e.stopPropagation()}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='body1'
                color='primary'
              >
                {[result.heading, result.title].filter(Boolean).join(': ')}
              </Typography>
              <Typography
                variant='caption'
                color='textSecondary'
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  '& strong': {
                    backgroundColor: warning[100],
                    padding: '0 2px',
                    borderRadius: 1,
                  },
                }}
              >
                {parse(getHighlightedText(result.highlights, 90))}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default SearchResults;
